import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register ScrollTrigger
gsap.registerPlugin(ScrollTrigger);

/**
 * Helper to dynamically inject single curtain overlay panel into image containers
 * if not already present in the HTML DOM.
 */
const ensureCurtainOverlay = (container) => {
  if (!container) return null;
  let overlay = container.querySelector('.image-reveal__overlay');
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.className = 'image-reveal__overlay';
    const curtainBlock = document.createElement('div');
    curtainBlock.className = 'image-reveal__col';
    overlay.appendChild(curtainBlock);
    container.appendChild(overlay);
  }
  return overlay;
};

/**
 * Initializes global GSAP animations.
 */
const initGsap = () => {
  // 1. Fade In Up Reveal Animation
  const fadeInUpElements = Array.from(document.querySelectorAll('.fade-in-up'))
    .filter(el => !el.closest('.hero-video-banner'));
  fadeInUpElements.forEach((el) => {
    gsap.fromTo(
      el,
      {
        opacity: 0,
        y: 40,
      },
      {
        opacity: 1,
        y: 0,
        duration: 1,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: el,
          start: 'top 88%',
          toggleActions: 'play none none none',
        },
      }
    );
  });

  // 2. Parallax Backgrounds for Reset Sections
  const resetSections = document.querySelectorAll('.reset-section');
  resetSections.forEach((section) => {
    const bg = section.querySelector('.reset-section__bg');
    if (bg) {
      gsap.fromTo(
        bg,
        { yPercent: -10 },
        {
          yPercent: 10,
          ease: 'none',
          scrollTrigger: {
            trigger: section,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
          },
        }
      );
    }
  });

  // 3. Single Curtain Opening Image Transition (ONLY for Section 2)
  const sec2ImageContainer = document.querySelector('.section-2-image-showcase');
  if (sec2ImageContainer) {
    const overlay = ensureCurtainOverlay(sec2ImageContainer);
    if (overlay) {
      const curtainBlock = overlay.querySelector('.image-reveal__col');
      const img = sec2ImageContainer.querySelector('img');

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sec2ImageContainer,
          start: 'top 82%',
          toggleActions: 'play reverse play reverse',
        },
      });

      tl.fromTo(
        curtainBlock,
        { yPercent: 0 },
        {
          yPercent: -100,
          duration: 1.1,
          ease: 'power3.inOut',
        }
      );

      if (img) {
        tl.fromTo(
          img,
          { scale: 1.12 },
          {
            scale: 1,
            duration: 1.2,
            ease: 'power2.out',
          },
          '<'
        );
      }
    }
  }

  // 4. Word-by-word text reveal for elements with .reveal-words
  const revealWordEls = document.querySelectorAll('.reveal-words');
  revealWordEls.forEach((el) => {
    // Avoid double wrapping if already processed
    if (!el.querySelector('.reveal-word')) {
      const rawHtml = el.innerHTML.trim();
      if (rawHtml) {
        const normalizedHtml = rawHtml.replace(/\s+/g, ' ');
        const parts = normalizedHtml.split(/(\s+)/);
        el.innerHTML = parts
          .map((part) => {
            if (/^\s+$/.test(part)) return ' ';

            if (part.includes('<br>') || part.includes('<br/>') || part.includes('<br />')) {
              return part
                .split(/(<br\s*\/?>)/gi)
                .map((sub) => {
                  if (/(<br\s*\/?>)/i.test(sub)) return sub;
                  if (sub.trim() === '') return ' ';
                  return `<span class="reveal-word">${sub}</span>`;
                })
                .join('');
            }

            return `<span class="reveal-word">${part}</span>`;
          })
          .join('');
      }
    }

    const wordNodes = el.querySelectorAll('.reveal-word');
    if (wordNodes.length === 0) return;

    const isPreLaunchBanner = el.closest('.pre-launch-banner');
    const isSec3Footer = el.closest('.section-3-footer-view');

    if (isPreLaunchBanner) {
      gsap.fromTo(
        wordNodes,
        { opacity: 0, y: 24 },
        {
          opacity: 1,
          y: 0,
          ease: 'power3.out',
          stagger: 0.07,
          duration: 0.85,
          delay: 0.3,
        }
      );
    } else if (isSec3Footer) {
      const sec3FooterSection = el.closest('.section-3-footer-view');

      // Ensure words remain 100% hidden upfront while scrolling prior sections
      gsap.set(wordNodes, { opacity: 0, y: 24 });

      gsap.to(wordNodes, {
        opacity: 1,
        y: 0,
        ease: 'power3.out',
        stagger: 0.1, // Slow, deliberate word-by-word pace
        duration: 1.3,
        delay: 2, // Waits 2 seconds after reaching the section before text starts revealing
        scrollTrigger: {
          trigger: sec3FooterSection,
          start: 'top 100%', // Triggers when section enters viewport
          toggleActions: 'play none none reverse',
        },
      });
    } else {
      gsap.fromTo(
        wordNodes,
        { opacity: 0, y: 22 },
        {
          opacity: 1,
          y: 0,
          ease: 'power3.out',
          stagger: 0.04,
          duration: 0.85,
          scrollTrigger: {
            trigger: el,
            start: 'top 85%',
            toggleActions: 'play none none none',
          },
        }
      );
    }
  });

  // 5. Special Gradient Line Draw-in from Right to Left
  const gradientLine = document.querySelector('.gradient-line');
  if (gradientLine) {
    gsap.fromTo(
      gradientLine,
      {
        scaleX: 0,
        transformOrigin: 'right center',
        opacity: 0,
      },
      {
        scaleX: 1,
        opacity: 1,
        duration: 1.6,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: gradientLine,
          start: 'top 85%',
          toggleActions: 'play none none none',
        },
      }
    );
  }

  // 6. Custom Animate-On-Scroll (data-aos) Reveal Animations (ensures Section 5 & Form remain visible)
  const aosElements = document.querySelectorAll('[data-aos]');
  aosElements.forEach((el) => {
    if (el.closest('.hero-video-banner')) return;

    const anim = el.getAttribute('data-aos') || 'fade-up';
    const delay = (parseFloat(el.getAttribute('data-aos-delay')) || 0) / 1000;

    let fromVars = { opacity: 0 };
    let toVars = {
      opacity: 1,
      duration: 1,
      delay: delay,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: el,
        start: 'top 90%',
        toggleActions: 'play none none none',
      },
    };

    if (anim === 'fade-up') {
      fromVars.y = 35;
      toVars.y = 0;
    } else if (anim === 'fade-down') {
      fromVars.y = -35;
      toVars.y = 0;
    } else if (anim === 'fade-right') {
      fromVars.x = -35;
      toVars.x = 0;
    } else if (anim === 'fade-left') {
      fromVars.x = 35;
      toVars.x = 0;
    } else if (anim === 'zoom-in') {
      fromVars.scale = 0.94;
      toVars.scale = 1;
    }

    gsap.fromTo(el, fromVars, toVars);
  });

  // 7. Banner Title Scroll Slide-up Animation
  const heroTitleGroup = document.querySelector('.hero-title-group');
  if (heroTitleGroup) {
    gsap.set(heroTitleGroup, { opacity: 1, y: 0 });

    gsap.to(heroTitleGroup, {
      y: -80,
      ease: 'none',
      scrollTrigger: {
        trigger: '.hero-video-banner',
        start: 'top top',
        end: 'bottom top',
        scrub: 2,
      },
    });
  }

  // Refresh ScrollTrigger calculation on load
  setTimeout(() => {
    ScrollTrigger.refresh();
  }, 100);

  window.addEventListener('load', () => {
    ScrollTrigger.refresh();
  });

  console.log('GSAP ScrollTrigger & custom data-aos animations initialized.');
};

export default initGsap;



